import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Upload, Loader2, CheckCircle2, X, Image as ImageIcon, ArrowLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import ServiceLineWithTemplate from './ServiceLineWithTemplate';
import { ExampleReceiptSVG } from './ExampleReceiptSVG';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT = 'image/png,image/jpeg,image/webp';

/**
 * Player flow — drop a Square receipt screenshot in. Backend OCRs it,
 * notifies staff, and credits the wallet once cashier approves.
 */
const SendReceiptDialog = ({ open, onClose, autoOpenPicker = false, returnPath = null }) => {
    const fileRef = useRef(null);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [amount, setAmount] = useState('');
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(null);

    // Auto-open the OS file / camera picker when the caller requests it
    // (i.e. the player just returned from PayPal and the dialog was
    // auto-opened for them). Small delay lets the dialog mount + settle so
    // the picker doesn't get eaten by the transition.
    useEffect(() => {
        if (!open || !autoOpenPicker) return;
        const t = setTimeout(() => {
            try { fileRef.current?.click(); } catch { /* ignore */ }
        }, 450);
        return () => clearTimeout(t);
    }, [open, autoOpenPicker]);

    const reset = () => {
        setFile(null); setPreview(null); setAmount(''); setNote('');
        setSubmitting(false); setDone(null);
        if (fileRef.current) fileRef.current.value = '';
    };
    const close = () => {
        onClose();
        setTimeout(reset, 250);
        // If the player started the load from a specific page (dice room,
        // ambassador dashboard, etc.), send them right back there. Only
        // navigates when we're currently somewhere else — otherwise the
        // dialog was already sitting on the right page.
        if (returnPath) {
            try {
                const here = window.location.pathname + window.location.search;
                if (here !== returnPath) {
                    window.location.href = returnPath;
                }
            } catch { /* ignore */ }
        }
    };

    const onPick = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!ACCEPT.split(',').includes(f.type)) {
            toast.error('Please pick a PNG, JPEG or WEBP image');
            return;
        }
        if (f.size > MAX_BYTES) {
            toast.error('Image is over 5 MB — please retake or compress');
            return;
        }
        setFile(f);
        const reader = new FileReader();
        reader.onload = () => setPreview(reader.result);
        reader.readAsDataURL(f);
    };

    const submit = async () => {
        if (!file) { toast.error('Snap or pick a Square receipt first'); return; }
        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append('receipt', file);
            if (amount) fd.append('claimed_amount', String(parseFloat(amount) || 0));
            if (note.trim()) fd.append('note', note.trim());
            const { data } = await axios.post(`${API}/square-receipts/upload`, fd, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setDone(data);
            toast.success('Receipt sent — staff will credit your wallet shortly.');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Upload failed — try again');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && close()}>
            <DialogContent
                className="bg-[#0c0c0c] border-[#39FF14]/30 max-w-md text-zinc-100"
                data-testid="send-receipt-dialog"
            >
                {!done && (
                    <>
                        <DialogHeader>
                            <DialogTitle className="text-[#39FF14] flex items-center gap-2 text-lg font-black">
                                <Camera className="w-5 h-5" /> Send PayPal receipt
                            </DialogTitle>
                            <DialogDescription className="text-zinc-400">
                                Upload a screenshot of your PayPal payment receipt. We scan it and staff will credit your wallet within a minute or two.
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-3 pt-1">
                            {/* PayPal example — ALWAYS visible with a big EXAMPLE
                                stamp so customers see the correct format off-the-rip
                                (Feb 2026 directive — too many wrong-format uploads). */}
                            <div
                                className="rounded-2xl border-2 border-[#39FF14]/60 bg-[#39FF14]/[0.06] p-3 space-y-2 relative overflow-hidden"
                                data-testid="receipt-example-block"
                            >
                                <div
                                    className="absolute -right-6 top-3 rotate-12 px-3 py-1 text-[13px] font-black tracking-[0.25em] shadow-lg z-10"
                                    style={{
                                        background: 'linear-gradient(90deg, #39FF14, #B8FF6B)',
                                        color: '#052500',
                                        borderRadius: '4px',
                                        border: '2px dashed #052500',
                                    }}
                                >
                                    EXAMPLE
                                </div>
                                <p className="text-[13px] text-white font-bold leading-snug pr-24">
                                    This is exactly what your PayPal receipt should look like ↓
                                </p>
                                <p className="text-[11px] text-zinc-300 leading-relaxed">
                                    Open PayPal → tap the payment you sent → tap the share icon → Save image.
                                    Screenshot must clearly show the <span className="text-[#39FF14] font-black">AMOUNT</span> and the{' '}
                                    <span className="text-[#39FF14] font-black">TRANSACTION ID</span>.
                                </p>
                                <ExampleReceiptSVG className="w-full max-w-[320px] mx-auto block rounded-lg shadow-2xl ring-2 ring-[#39FF14]/40" />
                                <p className="text-[10px] text-[#39FF14] text-center pt-1 font-black tracking-widest">
                                    ↑ SAMPLE ONLY · YOUR RECEIPT SHOULD LOOK LIKE THIS ↑
                                </p>
                            </div>

                            {/* File picker / preview */}
                            <button
                                type="button"
                                onClick={() => fileRef.current?.click()}
                                className={`w-full rounded-xl border-2 border-dashed transition-colors p-4 flex items-center justify-center gap-3 text-zinc-300 ${
                                    (autoOpenPicker && !file)
                                        ? 'border-[#39FF14] bg-[#39FF14]/15 animate-pulse shadow-[0_0_28px_rgba(57,255,20,0.5)]'
                                        : 'border-[#39FF14]/30 bg-[#39FF14]/5 hover:bg-[#39FF14]/10 hover:border-[#39FF14]/60'
                                }`}
                                data-testid="receipt-file-picker"
                                data-auto-picker={autoOpenPicker ? '1' : '0'}
                            >
                                {preview ? (
                                    <div className="flex items-center gap-3 w-full">
                                        <img src={preview} alt="receipt preview" className="w-16 h-16 rounded-lg object-cover" />
                                        <div className="flex-1 text-left">
                                            <div className="text-xs text-zinc-200 font-bold truncate">{file?.name}</div>
                                            <div className="text-[10px] text-zinc-500">Tap to replace</div>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <ImageIcon className={`w-6 h-6 ${(autoOpenPicker && !file) ? 'text-white' : 'text-[#39FF14]'}`} />
                                        <span className="text-sm font-bold">
                                            {(autoOpenPicker && !file)
                                                ? 'Tap here — pick your PayPal screenshot'
                                                : 'Tap to upload a screenshot'}
                                        </span>
                                    </>
                                )}
                            </button>
                            <input
                                ref={fileRef}
                                type="file"
                                accept={ACCEPT}
                                className="hidden"
                                onChange={onPick}
                                data-testid="receipt-file-input"
                            />

                            <div>
                                <Label className="text-zinc-300 text-xs">Amount you paid (helps us match faster)</Label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#39FF14] font-bold">$</span>
                                    <Input
                                        type="number" step="0.01" min="0.01"
                                        value={amount} onChange={(e) => setAmount(e.target.value)}
                                        className="bg-zinc-950 border-zinc-700 text-zinc-100 pl-7 font-mono"
                                        placeholder="20.00"
                                        data-testid="receipt-amount"
                                    />
                                </div>
                            </div>

                            <div>
                                <Label className="text-zinc-300 text-xs">Note (optional)</Label>
                                <Input
                                    value={note} onChange={(e) => setNote(e.target.value)}
                                    placeholder="e.g. paid 3:15 PM, my user is BlakeM"
                                    maxLength={120}
                                    className="bg-zinc-950 border-zinc-700 text-zinc-100"
                                    data-testid="receipt-note"
                                />
                            </div>

                            <Button
                                onClick={submit}
                                disabled={!file || submitting}
                                className="w-full bg-[#39FF14] text-zinc-900 hover:bg-[#5cff3a] font-black"
                                data-testid="receipt-submit"
                            >
                                {submitting ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                                ) : (
                                    <><Upload className="w-4 h-4 mr-2" /> Send to staff</>
                                )}
                            </Button>

                            {/* Fallback: pre-filled SMS template — for players who'd rather text */}
                            <ServiceLineWithTemplate amount={parseFloat(amount) || undefined} compact />

                            {/* Big obvious "Return to game" pill — replaces the
                                tiny "I didn't pay — close" link so players
                                never have to rely on the browser back button
                                (which triggered iOS Safari bfcache issues + a
                                confusing signout loop on production). */}
                            <button
                                type="button"
                                onClick={close}
                                className="w-full mt-2 flex items-center justify-center gap-2 py-3 rounded-2xl font-black text-[13px] tracking-wide transition-colors"
                                style={{
                                    background: 'linear-gradient(180deg, #1a1a1a, #0a0a0a)',
                                    border: '1.5px solid #FFD70055',
                                    color: '#FFD700',
                                    boxShadow: '0 4px 20px rgba(255,215,0,0.15)',
                                }}
                                data-testid="receipt-return-to-game"
                            >
                                <ArrowLeft className="w-4 h-4" strokeWidth={2.8} />
                                Return to game
                            </button>
                        </div>
                    </>
                )}

                {done && (
                    <>
                        <DialogHeader>
                            <DialogTitle className="text-[#39FF14] flex items-center gap-2 text-lg font-black">
                                <CheckCircle2 className="w-5 h-5" /> Receipt received
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-2 text-sm text-zinc-200">
                            <p>Staff are reviewing your receipt now. Your wallet will credit as soon as it&apos;s approved — usually a minute or two.</p>
                            {done.ocr_amount > 0 && done.ocr_confidence !== 'low' && (
                                <div className="mt-2 p-3 rounded-lg bg-zinc-900 border border-zinc-700" data-testid="receipt-detected-amount">
                                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Detected amount</div>
                                    <div className="font-mono font-black text-[#FFD700] text-xl">${done.ocr_amount.toFixed(2)}</div>
                                    <div className="text-[10px] text-zinc-500 mt-1">OCR confidence: {done.ocr_confidence}</div>
                                </div>
                            )}
                            {(done.ocr_amount <= 0 || done.ocr_confidence === 'low') && (
                                <div className="mt-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/40 text-amber-200 text-[12px]" data-testid="receipt-low-confidence">
                                    Our system couldn&apos;t read the amount automatically — no worries, staff will read it manually and credit your wallet shortly.
                                </div>
                            )}
                            <p className="text-[12px] text-zinc-500 pt-2">You&apos;ll get a notification the moment your wallet is credited.</p>
                        </div>
                        <Button
                            onClick={close}
                            className="w-full mt-3 font-black"
                            style={{
                                background: 'linear-gradient(180deg, #FFE55C 0%, #FFD700 50%, #B8860B 100%)',
                                color: '#0a0a0a',
                                boxShadow: '0 4px 14px rgba(255,215,0,0.35)',
                            }}
                            data-testid="receipt-done-close"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={2.8} />
                            Back to game
                        </Button>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default SendReceiptDialog;
